import { DayOfWeek, dayOfWeekFromJsIndex } from "../contracts/DayOfWeek";
import { WorkingHoursMode } from "../contracts/WorkingHoursMode";
import { SchedulePreferences } from "../models/SchedulePreferences";
import { WorkingHoursWindow } from "../models/WorkingHoursWindow";

/**
 * Returns the working-hours window that applies on a given JS Date in the user's local timezone,
 * or null if that day is non-working (PerDay mode with omitted day) or no preferences are set.
 */
export const getWindowForDate = (
  preferences: SchedulePreferences | undefined,
  date: Date | string
): WorkingHoursWindow | null => {
  if (!preferences || preferences.workingHours.length === 0) return null;
  const dateObj = typeof date === "string" ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return null;
  const day = dayOfWeekFromJsIndex(dateObj.getDay());

  if (preferences.workingHoursMode === WorkingHoursMode.SameEveryDay) {
    return preferences.workingHours.find((w) => w.dayOfWeek === null) ?? null;
  }

  // PerDay
  return preferences.workingHours.find((w) => w.dayOfWeek === day) ?? null;
};

/** "HH:MM" → minutes-from-midnight (NaN if malformed). */
export const hhmmToMinutes = (hhmm: string): number => {
  if (!hhmm) return NaN;
  const [h, m] = hhmm.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return NaN;
  return h * 60 + m;
};

/** minutes-from-midnight → "HH:MM". */
export const minutesToHHMM = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

/**
 * Default start for a NEW session: the next 30-minute slot that falls within
 * working hours — today from "now" if an in-hours slot still remains, otherwise
 * the start of the next working day (e.g. tomorrow 09:00). Looks up to a week
 * ahead. Returns null when there are no working hours to honor, so the caller
 * can fall back to its own default.
 */
export const getDefaultSessionStart = (
  preferences: SchedulePreferences | undefined,
  now: Date
): { date: Date; time: string } | null => {
  if (!preferences || preferences.workingHours.length === 0) return null;

  const SLOT_MINUTES = 30;
  for (let offset = 0; offset <= 7; offset++) {
    const day = new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset);
    const window = getWindowForDate(preferences, day);
    if (!window) continue;

    const startMin = hhmmToMinutes(window.start);
    const endMin = hhmmToMinutes(window.end);
    if (Number.isNaN(startMin) || Number.isNaN(endMin) || endMin <= startMin) continue;

    let candidateMin = startMin;
    if (offset === 0) {
      // Today: never suggest a past slot — round "now" up to the next slot boundary.
      const nowMin = now.getHours() * 60 + now.getMinutes();
      const rounded = Math.ceil(nowMin / SLOT_MINUTES) * SLOT_MINUTES;
      candidateMin = Math.max(startMin, rounded);
    }

    // The slot must start strictly before the window end to be a valid start.
    if (candidateMin >= endMin) continue;

    return { date: day, time: minutesToHHMM(candidateMin) };
  }
  return null;
};

/**
 * Whether a session at `time` (HH:MM) on `date` for `durationMinutes` fits inside the working-hours
 * window for that day. Returns false if no window applies (i.e. day is non-working).
 */
export const isTimeWithinWorkingHours = (
  preferences: SchedulePreferences | undefined,
  date: Date | string,
  time: string,
  durationMinutes: number
): boolean => {
  const window = getWindowForDate(preferences, date);
  if (!window) return false;
  const startMin = hhmmToMinutes(time);
  if (Number.isNaN(startMin)) return false;
  const endMin = startMin + durationMinutes;
  return startMin >= hhmmToMinutes(window.start) && endMin <= hhmmToMinutes(window.end);
};

export const ALL_DAYS: DayOfWeek[] = [
  DayOfWeek.Monday,
  DayOfWeek.Tuesday,
  DayOfWeek.Wednesday,
  DayOfWeek.Thursday,
  DayOfWeek.Friday,
  DayOfWeek.Saturday,
  DayOfWeek.Sunday,
];
